import React, { memo } from "react";
import "./sidebar.css";
import { SidebarRootMenu } from "../sidebar-root-menu/sidebar-root-menu";
import { useIsPreviewMode } from "../../../hooks/useIsPreviewMode";
import { useIsTransformMode } from "../../../hooks/useIsTransformMode";
import { SidebarTextPanel } from "../sidebar-text-panel/sidebar-text-panel";
import { MenuIds, useMenus } from "../../../hooks/useMenus";
import { SidebarPhotoPanel } from "../sidebar-photo-panel/sidebar-photo-panel";
import { SidebarFontFamiliesPanel } from "../sidebar-font-families-panel/sidebar-font-families-panel";
import { SidebarTextColorPanel } from "../sidebar-text-color-panel/sidebar-text-color-panel";
import { SidebarOptionsPanel } from "../sidebar-options-panel/sidebar-options-panel";
import { SidebarDesignsPanel } from "../sidebar-designs-panel";
import { useFeatureFlagToggle } from "../../../hooks/useFeatureFlags";
import { useIsOverviewMode } from "../../../hooks/useIsOverviewMode";

/**
 * Get the sidebar panel component
 * This could be improved, it now return a new component every time
 * TODO: Lazy load components, can we bundle it with the bottom menu version?
 */
function useGetSidebarPanel(): React.ReactNode {
	const { menuId } = useMenus();
	const allowDesktopPhotoMenus = useFeatureFlagToggle("allowDesktopPhotoMenus");

	switch (menuId) {
		// Photo
		// Show same panel for photo-selection and photo
		// I assume both will have a add zone / photo-selection menu
		case MenuIds.Photo:
		case MenuIds.PhotoRotate:
		case MenuIds.PhotoTransform:
		case MenuIds.PhotoZoneDepth:
		case MenuIds.PhotoSelection: {
			if (allowDesktopPhotoMenus) {
				return <SidebarPhotoPanel />;
			}
			return null;
		}
		// Color Effects
		case MenuIds.PhotoBrightness:
		case MenuIds.PhotoContrast:
		case MenuIds.PhotoSaturation:
		case MenuIds.PhotoFilters:
		case MenuIds.PhotoEditing: {
			return null;
		}

		// Text
		case MenuIds.Text:
		case MenuIds.TextStyle:
		case MenuIds.TextHAlignment:
		case MenuIds.TextVAlignment:
		case MenuIds.TextSize:
		case MenuIds.TextZoneAdd:
		case MenuIds.TextZoneDepth:
		case MenuIds.TextValue:
			return <SidebarTextPanel />;

		case MenuIds.TextColor:
			return <SidebarTextColorPanel />;

		case MenuIds.FontFamilies:
			return <SidebarFontFamiliesPanel />;

		case MenuIds.Layouts:
			return null;

		case MenuIds.Qr:
		case MenuIds.QrAdd:
		case MenuIds.QrEdit: {
			return null;
		}

		case MenuIds.Symbols:
		case MenuIds.SymbolsList:
		case MenuIds.SymbolZoneDepth:
			return null;

		case MenuIds.Designs:
			return <SidebarDesignsPanel />;

		case MenuIds.Options:
		case MenuIds.AlternativeProducts:
		case MenuIds.ProductOptions:
		case MenuIds.DateSelection:
			return <SidebarOptionsPanel />;

		case MenuIds.Backgrounds:
		case MenuIds.BackgroundColor:
		case MenuIds.BackgroundPattern:
			return null;

		// No sidebar menu
		case undefined:
		case MenuIds.Root:
		case MenuIds.Map:
		case MenuIds.Events:
			break;
		default:
			eXpress.utils.exhaustiveFail(menuId);
	}

	return undefined;
}

export const Sidebar = memo(function Sidebar() {
	const [isTransformMode] = useIsTransformMode();
	const [isPreviewMode] = useIsPreviewMode();
	const [isOverviewMode] = useIsOverviewMode();

	const panel = useGetSidebarPanel();

	// Don't Show in some cases
	if (isPreviewMode || isTransformMode || isOverviewMode) return null;

	return (
		<div className="sidebar">
			<SidebarRootMenu />
			{panel && <div className="sidebar__panel">{panel}</div>}
		</div>
	);
});
